import { supabase } from "@/lib/supabase/browser-client"
import { Tables } from "@/supabase/types"
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import { Dispatch, SetStateAction } from "react"
import { printObject } from '@/lib/utils'
import { HandlerConfig } from "../base-subscription-manager"
import { getToolById, getToolWorkspacesByToolId } from "@/db/tools"
import { v4 as uuidv4 } from "uuid"
import { createClientLogger } from "@/utils/logger/clientLogger"
import { getMcpById, getMcpWorkspacesByMcpId } from "@/db/mcps"

const logger = createClientLogger('realtime.mcps', supabase)

type Row = Tables<'mcps'>
type WsRow = Tables<'mcp_workspaces'>

const appliesToWorkspace = async (workspaceId:string | null, id: string): Promise<boolean> => {
	if (!workspaceId) return false
	const mcpWorkspaces = await getMcpWorkspacesByMcpId(supabase, id)
	return mcpWorkspaces.workspaces.some(ws => ws.id === workspaceId)
}

/**
 * This method doesn't handle insert since at that time the workspaces are not known.
 * It also doesn't handle delete since workspace relationship join table is better suited since it covers all cases
 * @param workspaceId
 * @param setMcps
 * @returns
 */
export function makeMcpChangeHandler(
	setMcps: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<Row> {
	const uuid = uuidv4()
	return {
		key: `mcp-changes-${uuid}`,
		channelName: `mcp-changes-${uuid}`,
		events: ['UPDATE'],
		schema: 'public',
		table: 'mcps',
		handler: async (payload: RealtimePostgresChangesPayload<Row>) => {
			if (payload.eventType === 'UPDATE') {
				const mcp = payload.new as Row
				//here not checking for workspace since will update only if found and it avoid async db call
				setMcps(prev => {
					const exists = prev.some(p => p.id === mcp.id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling MPC update:${printObject(mcp)}`)
					return prev.map(p => p.id === mcp.id ? mcp : p)
				}
				)
			}
		},
		inactivityThreshold: 1_800_000
	}
}

/**
 * This method doesn't handle update since the relationship join table is not designed to mutate in that way
 * @param workspaceId
 * @param setMcps
 * @returns
 */
export function makeMcpWorkspaceHandler(
	workspaceIdRef: React.RefObject<string | null>,
	setMcps: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<WsRow> {
	const uuid = uuidv4()
	return {
		key: `mcp-ws-${uuid}`,
		channelName: `mcp-ws-${uuid}`,
		events: ['INSERT', 'DELETE'],
		schema: 'public',
		table: 'mcp_workspaces',
		handler: async (payload: RealtimePostgresChangesPayload<WsRow>) => {
			const workspaceId = workspaceIdRef.current
			if (payload.eventType == "INSERT") {
				const mcpWs = payload.new as WsRow
				const id = mcpWs.mcp_id
				if (await appliesToWorkspace(workspaceId, id)) {
					const mcp = await getMcpById(supabase, id)
					if (mcp) {
						setMcps(prev => {
							if (prev.some(p => p.id === mcp.id)) return prev
							logger.info(`Handling MPC Insert:${printObject(mcp)}`)
							return [...prev, mcp]
						})
					}
				}
			}
			else if (payload.eventType == "DELETE") {
				const mcpWs = payload.old as WsRow
				const id = mcpWs.mcp_id
				setMcps(prev => {
					const exists = prev.some(p => p.id === id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling MCP Delete:${id}`)
					return prev.filter(p => p.id !== id)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}